import { API_DOMAIN } from '@constants';
import type { Member, TeamMember, TeamMemberTourRights } from '@types';
import axios from '@utils/axios';
import redirectToLogin from '@utils/redirectToLogin';
import redirectToNA from '@utils/redirectToNA';
import { createDomain, forward, sample } from 'effector';

const domain = createDomain('member');

type TourRights = {
  expired: 0 | 1;
  /** 1: 入门版, 2: 创作版, 3: 专业版, 4: 企业高级版, 5: 企业旗舰版, 7: 合作定制版 */
  level: number;
}[];

export const $member = domain.store<Partial<Member>>({});
export const $tourRights = domain.store<TourRights>([]);
export const $teamMemberData = domain.store<Partial<TeamMember>>({ role: 0 });
export const $teamMemberTourRights = domain.store<
  Partial<TeamMemberTourRights>
>({});

const memberRequest = domain.effect(async () => {
  return axios.get<Member>(`${API_DOMAIN}/member/me`);
});

const tourRightRequest = domain.effect(async () => {
  return axios.get<TourRights>(`${API_DOMAIN}/member/my/tour-rights`);
});

const teamMemberDataRequest = domain.effect(async () => {
  const teamMemmberData = await axios.get<TeamMember>(
    `${API_DOMAIN}/team/member/data`
  );
  if (teamMemmberData.status !== 1) {
    throw new Error('团队成员状态异常');
  }
  return teamMemmberData;
});

const teamMemberTourRightsRequest = domain.effect(async () => {
  const teamTourRights = await axios.get<TeamMemberTourRights>(
    `${API_DOMAIN}/team/member/tour-rights`
  );
  if (teamTourRights.valid !== 1) {
    throw new Error('团队成员漫游工具权限无效');
  }
  if (teamTourRights.access < 1) {
    // 编辑工具需要权限1或以上才能访问
    throw new Error('团队成员漫游工具权限不足');
  }
  return teamTourRights;
});

// null 为请求完成前状态
export const $isVip = domain.store<boolean | null>(null);
export const $isTeamAccount = domain.store<boolean | null>(null);
export const $isOwner = domain.store<boolean | null>(null);

const fetchMember = domain.effect(async () => {
  const member = await memberRequest();
  const teamMemmberData = await teamMemberDataRequest();
  // 如果是企业所有者, 需要判断Level. 如果是成员, 则都视为VIP并请求成员权限
  if (teamMemmberData.role === 0) {
    const tourRights = await tourRightRequest();
    const isVip = tourRights.some((x) => x.level >= 3 && x.expired === 0);
    const isTeamAccount = tourRights.some(
      (x) => x.level >= 4 && x.expired === 0
    );
    return { member, isVip, isTeamAccount, isOwner: true };
  }
  await teamMemberTourRightsRequest();
  return { member, isVip: true, isTeamAccount: true, isOwner: false };
});

memberRequest.fail.watch(redirectToLogin);
tourRightRequest.fail.watch(redirectToLogin);
teamMemberDataRequest.fail.watch(redirectToNA);
teamMemberTourRightsRequest.fail.watch(redirectToNA);

forward({ from: memberRequest.doneData, to: $member });
forward({ from: tourRightRequest.doneData, to: $tourRights });
forward({ from: teamMemberDataRequest.doneData, to: $teamMemberData });
forward({
  from: teamMemberTourRightsRequest.doneData,
  to: $teamMemberTourRights,
});
forward({ from: fetchMember.doneData.map((s) => s.isVip), to: $isVip });
forward({
  from: fetchMember.doneData.map((s) => s.isTeamAccount),
  to: $isTeamAccount,
});
forward({
  from: fetchMember.doneData.map((s) => s.isOwner),
  to: $isOwner,
});

const setLastOwnerId = domain.event<TeamMember['ownerId'] | null>();
const $lastOwnerId = domain
  .store<TeamMember['ownerId'] | null>(null)
  .on(setLastOwnerId, (_, payload) => payload);

sample({
  source: $lastOwnerId,
  clock: teamMemberDataRequest.doneData,
  fn: (lastOwnerId, data) => {
    const { ownerId } = data;
    return { lastOwnerId, ownerId };
  },
}).watch(({ lastOwnerId, ownerId }) => {
  // 标签页激活时, 检测一下当前用户是否变更
  setLastOwnerId(ownerId);
  if (lastOwnerId !== null && lastOwnerId !== ownerId) {
    window.location.reload();
  }
});

export const $memberApi = { fetchMember, teamMemberDataRequest };
